/** Both records share a business list; their posting and confirmation rules stay distinct. */
export function orderFeed(shared, local, workspaceId) {
    return [
        ...shared.map((order) => ({
            ...order,
            source: 'SHARED',
            side: order.seller_id === workspaceId ? 'SELL' : 'BUY',
            peer: order.seller_id === workspaceId ? order.buyer_name : order.seller_name,
            posted: order.seller_id === workspaceId ? order.shipped : order.accepted,
        })),
        ...local.map((order) => ({
            ...order,
            source: 'LOCAL',
            side: order.kind === 'SALE' ? 'SELL' : 'BUY',
            peer: order.counterparty,
            // A manual record has no counterparty confirmation step.
            status: Number(order.remaining) === 0 ? 'CLOSED' : 'CONFIRMED',
            shipped: order.kind === 'SALE' ? order.posted : '0',
            accepted: order.kind === 'PURCHASE' ? order.posted : '0',
        })),
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime() ||
        a.id.localeCompare(b.id));
}
export function feedStatus(order) {
    if (order.source === 'SHARED')
        return '';
    // Self-recorded orders run goods → reconciliation → money, all on this enterprise's side.
    return nextStep(order).label;
}
const pct = (part, whole) => {
    const w = Number(whole);
    return w > 0 ? Math.max(0, Math.min(100, Math.round((Number(part) / w) * 100))) : 0;
};
/** Works out where an order stands and whose turn it is, for list views. */
export function nextStep(order) {
    const selling = order.side === 'SELL';
    if (order.source === 'LOCAL') {
        const multi = (order.line_count ?? 1) > 1;
        const progress = multi
            ? pct(order.lines_done ?? 0, order.line_count ?? 1)
            : pct(order.posted, order.quantity);
        const verb = selling ? '出库' : '入库';
        // Goods first, then this enterprise reconciles and records the money itself:
        // 出库 → (客户签收) → 对账 → 结清 for sales, 入库 → 对账 → 付款 for purchases.
        if (order.settlement_amount == null) {
            if (order.status !== 'CLOSED')
                return {
                    label: Number(order.posted) > 0 ? `部分${verb}` : `待${verb}`,
                    tone: 'warning',
                    mine: true,
                    action: 'POST',
                    actionLabel: `登记${verb}`,
                    progress,
                    finished: false,
                };
            return {
                label: '待对账',
                tone: 'warning',
                mine: true,
                action: 'SETTLE',
                actionLabel: '去对账',
                progress: 100,
                finished: false,
            };
        }
        if (Number(order.paid ?? 0) < Number(order.settlement_amount))
            return {
                label: selling ? '待收款' : '待付款',
                tone: 'warning',
                mine: true,
                action: selling ? 'COLLECT' : 'PAY',
                actionLabel: selling ? '登记收款' : '登记付款',
                progress,
                finished: false,
            };
        return { label: '已完成', tone: 'success', mine: false, progress, finished: true };
    }
    const shippedPct = pct(order.shipped, order.quantity);
    const acceptedPct = pct(order.accepted, order.quantity);
    const progress = selling ? shippedPct : acceptedPct;
    if (order.status === 'CANCELLED')
        return { label: '已取消', tone: 'neutral', mine: false, progress: 0, finished: true };
    if (order.status === 'PENDING') {
        const mineConfirmed = selling ? order.seller_confirmed : order.buyer_confirmed;
        return mineConfirmed
            ? { label: '等对方确认', tone: 'info', mine: false, progress, finished: false }
            : {
                label: '待我确认',
                tone: 'warning',
                mine: true,
                action: 'CONFIRM',
                actionLabel: '去确认',
                progress,
                finished: false,
            };
    }
    if ((order.open_issues ?? 0) > 0)
        return {
            label: '差异 / 退货待处理',
            tone: 'warning',
            mine: true,
            action: 'ISSUE',
            actionLabel: '去处理',
            progress,
            finished: false,
        };
    if (order.status === 'CONFIRMED') {
        if (selling) {
            if (shippedPct < 100)
                return {
                    label: shippedPct > 0 ? `已发 ${shippedPct}%` : '待发货',
                    tone: 'warning',
                    mine: true,
                    action: 'SHIP',
                    actionLabel: '去发货',
                    progress,
                    finished: false,
                };
            return { label: '等对方收货', tone: 'info', mine: false, progress, finished: false };
        }
        if (Number(order.shipped) > Number(order.accepted))
            return {
                label: '到货待验收',
                tone: 'warning',
                mine: true,
                action: 'RECEIVE',
                actionLabel: '去收货',
                progress,
                finished: false,
            };
        return {
            label: acceptedPct > 0 ? `已收 ${acceptedPct}%` : '等对方发货',
            tone: 'info',
            mine: false,
            progress,
            finished: false,
        };
    }
    // Delivery is complete; what remains is reconciliation.
    // On a monthly statement, reconciliation and payment happen there rather than per order.
    if (order.statement_status === 'PENDING')
        return { label: '对账中', tone: 'info', mine: false, progress: 100, finished: false };
    if (order.statement_status === 'CONFIRMED' &&
        Number(order.paid ?? 0) < Number(order.settlement_amount ?? 0))
        return { label: '月结待结清', tone: 'info', mine: false, progress: 100, finished: false };
    const settledByMe = selling
        ? order.settlement_seller_confirmed
        : order.settlement_buyer_confirmed;
    const settledByThem = selling
        ? order.settlement_buyer_confirmed
        : order.settlement_seller_confirmed;
    if (!order.has_settlement)
        return {
            label: '待对账',
            tone: 'warning',
            mine: true,
            action: 'SETTLE',
            actionLabel: '去对账',
            progress: 100,
            finished: false,
        };
    if (!settledByMe)
        return {
            label: '待我确认对账',
            tone: 'warning',
            mine: true,
            action: 'SETTLE',
            actionLabel: '去对账',
            progress: 100,
            finished: false,
        };
    if (!settledByThem)
        return { label: '等对方确认对账', tone: 'info', mine: false, progress: 100, finished: false };
    // Reconciled; what remains is the money changing hands off-platform.
    if (Number(order.paid ?? 0) < Number(order.settlement_amount ?? 0)) {
        const pending = (order.pending_claims ?? 0) > 0;
        if (selling)
            return pending
                ? {
                    label: '待确认收款',
                    tone: 'warning',
                    mine: true,
                    action: 'COLLECT',
                    actionLabel: '确认收款',
                    progress: 100,
                    finished: false,
                }
                : { label: '等待买方付款', tone: 'info', mine: false, progress: 100, finished: false };
        return pending
            ? { label: '等卖方确认收款', tone: 'info', mine: false, progress: 100, finished: false }
            : {
                label: '待付款',
                tone: 'warning',
                mine: true,
                action: 'PAY',
                actionLabel: '去付款',
                progress: 100,
                finished: false,
            };
    }
    return { label: '已完成', tone: 'success', mine: false, progress: 100, finished: true };
}
/**
 * Which part of its life an order is in, for the home-page counts:
 * - `delivery`: confirmed and goods still moving (shared orders not fully received;
 *   self-recorded orders not fully shipped or received and not yet reconciled);
 * - `money`: goods delivered, but not yet reconciled or not yet fully received / paid,
 *   including shared orders on a monthly statement or with a discrepancy still open;
 * - null: waiting for confirmation, finished, or cancelled.
 */
export function orderPhase(order) {
    if (order.status === 'PENDING' || order.status === 'CANCELLED')
        return null;
    if (nextStep(order).finished)
        return null;
    const reconciled = order.source === 'LOCAL' && order.settlement_amount != null;
    return order.status === 'CLOSED' || reconciled ? 'money' : 'delivery';
}
/** Days past the delivery date for orders whose goods have not all moved yet; 0 if on time. */
export function overdueDays(order, step, today = new Date()) {
    if (step.finished || step.progress >= 100 || !order.delivery_date)
        return 0;
    const due = new Date(order.delivery_date);
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    return Math.max(0, Math.round((start.getTime() - dueDay.getTime()) / 86400000));
}
